import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { jobsAPI, applicationsAPI } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowLeft, MapPin, Briefcase, Star, ShieldCheck, Crown, CheckCircle2,
  Send, Building2, Calendar, Banknote, Clock, Users, AlertTriangle, X,
  Lightbulb, FileText, Lock, Mail, ArrowRight, Search, ChevronDown, BellPlus, MessageCircle, Share2,
  Sparkles,
} from 'lucide-react';
import FavouriteButton from '@/components/FavouriteButton';
import { JobWorkInfo } from '@/components/JobWorkInfo';
import { ShareJobButtons } from '@/components/ShareJobButtons';
import { JobSafetyNotice } from '@/components/JobSafetyNotice';
import apiClient from '@/utils/api';

// Pick the localised job field with PT fallback (backend stores title_pt/_en/_fr, etc.)
// Special-case for `category` field where the PT source is stored as `category` (no _pt suffix).
function pickLocalized(job, field, lang) {
  if (!job) return '';
  const lng = (lang || 'pt').slice(0, 2);
  const key = `${field}_${lng}`;
  if (job[key] && String(job[key]).trim()) return job[key];
  if (field === 'category') return job.category || '';
  return job[`${field}_pt`] || '';
}

function metaDescriptionFrom(job, lang) {
  const parts = [];
  if (job.company_name) parts.push(job.company_name);
  if (job.location) parts.push(job.location);
  if (job.category) parts.push(job.category);
  const head = parts.join(' · ');
  const desc = (pickLocalized(job, 'description', lang) || '').replace(/\s+/g, ' ').trim().slice(0, 140);
  return `${head}. ${desc}`.slice(0, 200);
}

function isJobExpired(job) {
  if (!job?.expires_at) return false;
  try { return new Date(job.expires_at).getTime() < Date.now(); } catch { return false; }
}

/**
 * Renders free-text content into clean HTML.
 * - Lines starting with •, -, *, or "N." become <li> inside a <ul>
 * - Blank-line-separated paragraphs become <p>
 * - Preserves natural paragraph rhythm without raw whitespace-pre-wrap blob
 */
function RichText({ text }) {
  if (!text) return null;
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let buffer = [];
  let listBuffer = [];

  const flushPara = () => {
    if (buffer.length) {
      const para = buffer.join(' ').trim();
      if (para) blocks.push({ type: 'p', text: para });
      buffer = [];
    }
  };
  const flushList = () => {
    if (listBuffer.length) {
      blocks.push({ type: 'ul', items: listBuffer });
      listBuffer = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); continue; }
    const bullet = line.match(/^[•\-*–]\s+(.*)/) || line.match(/^\d+[.)]\s+(.*)/);
    if (bullet) {
      flushPara();
      listBuffer.push(bullet[1]);
    } else {
      flushList();
      buffer.push(line);
    }
  }
  flushPara(); flushList();

  return (
    <div className="space-y-3 text-[15px] text-slate-800 leading-[1.7]">
      {blocks.map((b, i) =>
        b.type === 'p' ? (
          <p key={i}>{b.text}</p>
        ) : (
          <ul key={i} className="space-y-1.5 pl-1">
            {b.items.map((it, j) => (
              <li key={j} className="flex gap-2.5">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-slate-400 flex-none" aria-hidden="true"></span>
                <span>{it}</span>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}

export default function JobDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, userType, setUser } = useAuth();
  const [quickState, setQuickState] = useState('idle'); // idle | working | done | nocv | error
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'pt').slice(0, 2);
  const dateLocale = lang === 'fr' ? 'fr-FR' : lang === 'en' ? 'en-US' : 'pt-PT';
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [applied, setApplied] = useState(false);
  const [similar, setSimilar] = useState([]);

  const formatPostedDate = useCallback((d) => {
    if (!d) return null;
    try {
      const date = new Date(d);
      const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
      if (days === 0) return t('jobDetails.postedToday');
      if (days === 1) return t('jobDetails.postedYesterday');
      if (days < 30) return t('jobDetails.postedDays', { days });
      return t('jobDetails.postedOn', { date: date.toLocaleDateString(dateLocale) });
    } catch { return null; }
  }, [t, dateLocale]);

  useEffect(() => {
    // Share stats: count one opening per shared link (?src=wa|fb|link|native) per session
    const src = new URLSearchParams(window.location.search).get('src');
    if (src && !sessionStorage.getItem(`share-open-${id}`)) {
      sessionStorage.setItem(`share-open-${id}`, '1');
      apiClient.post(`/jobs/${id}/share-open`, { src }).catch(() => {});
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    jobsAPI.getById(id)
      .then((r) => setJob(r.data))
      .catch(() => setFeedback(t('jobDetails.loadError')))
      .finally(() => setLoading(false));
  }, [id, t]);

  useEffect(() => {
    if (userType === 'candidate' && user) {
      applicationsAPI.getMy()
        .then((r) => {
          if (Array.isArray(r.data) && r.data.some((a) => a.job_id === id)) setApplied(true);
        })
        .catch(() => {});
    }
  }, [id, user, userType]);

  // Load similar jobs (same category, limit 4)
  useEffect(() => {
    if (!job) return;
    const params = { limit: 5 };
    if (job.category) params.category = job.category;
    jobsAPI.getPublic(params)
      .then((r) => setSimilar((r.data || []).filter((j) => j.id !== job.id).slice(0, 4)))
      .catch(() => {});
  }, [job]);

  const isCandidate = userType === 'candidate';
  const hasCV = Boolean(user?.cv_url);
  const expired = job && isJobExpired(job);

  const handleApplyClick = () => {
    if (!user) {
      // Soft prompt: open modal in unauth state instead of immediate redirect
      navigate(`/candidate/login?redirect=/jobs/${id}`);
      return;
    }
    if (!isCandidate) {
      setFeedback(t('jobDetails.applyAuthOnly'));
      return;
    }
    if (expired) return;
    setMessage('');
    setFeedback('');
    setShowApplyModal(true);
  };

  // Quick apply: attach the Trabadju AI CV and generate a job-tailored cover letter (existing endpoints only)
  const quickApply = async () => {
    setQuickState('working');
    setFeedback('');
    try {
      const { data: cv } = await apiClient.get('/cv/me');
      if (!cv?.personal?.full_name) { setQuickState('nocv'); return; }
      const lang2 = (lang || 'pt').slice(0, 2);
      const { data: att } = await apiClient.post('/cv/me/use-in-applications', { lang: lang2 }, { timeout: 200000 });
      if (att?.cv_url && setUser) setUser((u) => (u ? { ...u, cv_url: att.cv_url } : u));
      const today = new Date().toLocaleDateString(lang2 === 'en' ? 'en-GB' : lang2 === 'fr' ? 'fr-FR' : 'pt-PT', { day: 'numeric', month: 'long', year: 'numeric' });
      const jobText = `${jobTitle}\n${job.company_name || ''}\n${(jobDescription || '').slice(0, 3000)}\n\n(Data de hoje / today's date: ${today} — usar esta data na carta)`;
      const { data: gen } = await apiClient.post('/cv/ai/cover-letter', { cv, lang: lang2, job_description: jobText }, { timeout: 120000 });
      if (gen?.letter) setMessage(gen.letter);
      setQuickState('done');
    } catch (e) {
      setQuickState('error');
      setFeedback(e?.response?.data?.detail || t('jobDetails.quickApplyError'));
    }
  };

  const submitApplication = async () => {
    setSubmitting(true);
    setFeedback('');
    try {
      await applicationsAPI.apply({
        job_id: id,
        message: message.trim() || null,
      });
      setApplied(true);
      // Keep modal open to show success state — user closes manually
    } catch (e) {
      setFeedback(e?.response?.data?.detail || t('jobDetails.applyError'));
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-300 border-t-slate-900"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <main className="max-w-2xl mx-auto py-24 px-4 text-center">
        <Helmet><title>{`${t('jobDetails.notFoundTitle')} · Trabadju`}</title></Helmet>
        <h1 className="text-2xl font-bold tracking-tight mb-2 text-slate-900">{t('jobDetails.notFoundTitle')}</h1>
        <p className="text-slate-600 mb-6">{t('jobDetails.notFoundBody')}</p>
        <Button onClick={() => navigate('/jobs/search')} className="bg-slate-900 hover:bg-slate-800 text-white">{t('jobDetails.notFoundCta')}</Button>
      </main>
    );
  }

  const postedLabel = formatPostedDate(job.created_at);
  const employmentLabel = job.employment_type
    ? t(`jobDetails.employment.${job.employment_type}`, { defaultValue: job.employment_type })
    : null;
  const salary = job.salary_range_min && job.salary_range_max
    ? `${Number(job.salary_range_min).toLocaleString(dateLocale)} – ${Number(job.salary_range_max).toLocaleString(dateLocale)} ${job.currency || 'CVE'}`
    : null;
  const jobTitle = pickLocalized(job, 'title', lang);
  const jobDescription = pickLocalized(job, 'description', lang);
  const jobRequirements = pickLocalized(job, 'requirements', lang);
  const jobCategory = pickLocalized(job, 'category', lang) || job.category || '';
  const jobBenefits = job.benefits_pt;

  // Reusable apply CTA (function, not nested component, to avoid re-mounts)
  const applyCard = (compact = false) => (
    <Card className={`border-slate-200 ${compact ? 'p-5' : 'p-6'}`} data-testid={`apply-card-${compact ? 'sidebar' : 'inline'}`}>
      {applied ? (
        <div data-testid="apply-card-applied">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50 mb-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" aria-hidden="true" />
          </div>
          <h3 className="text-[17px] font-bold text-slate-900 mb-1 tracking-tight">{t('jobDetails.applySent')}</h3>
          <p className="text-[13px] text-slate-600 leading-relaxed mb-4">
            <Trans i18nKey="jobDetails.applySentBody" components={{ strong: <strong /> }} />
          </p>
          <Button onClick={() => navigate('/my-applications')} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold" data-testid="see-my-apps-btn">
            {t('jobDetails.seeMyApps')}
          </Button>
          <button
            type="button"
            onClick={() => navigate('/jobs/search')}
            className="w-full text-[12.5px] font-semibold text-slate-600 hover:text-slate-900 mt-2 py-2 inline-flex items-center justify-center gap-1.5"
          >
            <Search className="h-3.5 w-3.5" aria-hidden="true" />{t('jobDetails.searchMore')}
          </button>
        </div>
      ) : expired ? (
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-600 flex-none" aria-hidden="true" />
          <div>
            <h3 className="text-base font-bold text-slate-900 mb-1">{t('jobDetails.expiredTitle')}</h3>
            <p className="text-[13px] text-slate-600 leading-relaxed mb-3">{t('jobDetails.expiredBody')}</p>
            <Button size="sm" onClick={() => navigate('/jobs/search')} className="bg-slate-900 hover:bg-slate-800 text-white">{t('jobDetails.expiredCta')}</Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="apply-card-trust-pills">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-emerald-50 text-emerald-800 rounded">
              <CheckCircle2 className="h-3 w-3" aria-hidden="true" />{t('jobDetails.pillFree')}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-blue-50 text-blue-800 rounded">
              {t('jobDetails.pillDirect')}
            </span>
          </div>
          <h3 className="text-[17px] font-bold text-slate-900 mb-1 tracking-tight">{t('jobDetails.applyCardTitle')}</h3>
          <p className="text-[13px] text-slate-600 leading-relaxed mb-4">
            {isCandidate
              ? (hasCV
                  ? <><CheckCircle2 className="h-3.5 w-3.5 inline text-emerald-600 mr-1" /><Trans i18nKey="jobDetails.applyCardBodyCv" components={{ strong: <strong /> }} /></>
                  : t('jobDetails.applyCardBodyNoCv'))
              : user ? t('jobDetails.applyCardBodyAuth') : t('jobDetails.applyCardBodyGuest')}
          </p>
          <Button
            onClick={handleApplyClick}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11 font-semibold shadow-sm"
            data-testid={compact ? 'apply-btn-sidebar' : 'apply-btn'}
          >
            <Send className="h-4 w-4 mr-2" aria-hidden="true" />
            {t('jobDetails.applyButton')}
          </Button>
          {isCandidate && !hasCV && (
            <p className="mt-3 text-[12px] text-slate-500">
              <Lightbulb className="h-3 w-3 inline text-amber-500 mr-1" aria-hidden="true" />
              <Link to="/candidate/profile" className="text-blue-700 hover:underline font-medium">{t('jobDetails.addCvLink')}</Link> {t('jobDetails.addCvSuffix')}
            </p>
          )}

          {/* How it works */}
          <div className="mt-5 pt-5 border-t border-slate-100" data-testid="apply-how-it-works">
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-3">{t('jobDetails.howItWorks')}</p>
            <ol className="space-y-2.5">
              <li className="flex gap-3">
                <span className="flex-none w-5 h-5 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center mt-0.5">1</span>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-slate-900 leading-tight">{t('jobDetails.step1Title')}</p>
                  <p className="text-[12px] text-slate-500 leading-snug">{t('jobDetails.step1Body')}</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-none w-5 h-5 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center mt-0.5">2</span>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-slate-900 leading-tight">{t('jobDetails.step2Title')}</p>
                  <p className="text-[12px] text-slate-500 leading-snug">{t('jobDetails.step2Body')}</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-none w-5 h-5 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center mt-0.5">3</span>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-slate-900 leading-tight">{t('jobDetails.step3Title')}</p>
                  <p className="text-[12px] text-slate-500 leading-snug">{t('jobDetails.step3Body')}</p>
                </div>
              </li>
            </ol>
          </div>

          {/* Trust line */}
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-start gap-2 text-[11.5px] text-slate-500 leading-snug">
            <Lock className="h-3.5 w-3.5 mt-0.5 flex-none text-slate-400" aria-hidden="true" />
            <span>{t('jobDetails.privacyLine')} <Link to="/privacidade" className="text-slate-700 hover:underline">{t('jobDetails.privacyLink')}</Link>.</span>
          </div>

          {/* Alerts link */}
          {!applied && (
            <button
              type="button"
              onClick={() => navigate(user ? '/candidate/profile?tab=alerts' : '/candidate/register')}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 text-[12.5px] font-semibold text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded-md py-2 transition"
              data-testid="alerts-link"
            >
              <BellPlus className="h-3.5 w-3.5" aria-hidden="true" />
              {t('jobDetails.alertsLink')}
            </button>
          )}

          {feedback && !showApplyModal && (
            <Alert variant="destructive" className="mt-3"><AlertDescription>{feedback}</AlertDescription></Alert>
          )}
        </>
      )}
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>{`${jobTitle}${job.company_name ? ` · ${job.company_name}` : ''} · Trabadju`}</title>
        <meta name="description" content={metaDescriptionFrom(job, lang)} />
        <link rel="canonical" href={`https://trabadju.cv/jobs/${id}`} />
      </Helmet>

      <main className="min-h-screen bg-slate-50 pb-32 lg:pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-600 hover:text-slate-900 mb-5 transition"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t('jobDetails.back')}
          </button>

          {expired && !applied && (
            <Alert className="mb-5 border-amber-300 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              <AlertDescription className="text-amber-900 text-[13px]">{t('jobDetails.expiredAlert')}</AlertDescription>
            </Alert>
          )}

          <div className="grid lg:grid-cols-[1fr_340px] gap-6 lg:gap-8 items-start">
            {/* ─── Main article ─── */}
            <article className="min-w-0" data-testid="job-details-card">
              {/* Header */}
              <header className="mb-6 relative bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                {/* Top accent bar */}
                <div
                  className={`absolute inset-x-0 top-0 h-1 ${job.is_featured ? 'bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500' : 'bg-gradient-to-r from-blue-600 via-indigo-500 to-emerald-500'}`}
                  aria-hidden="true"
                />
                <div className="p-5 sm:p-7">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    {/* Badges row */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {job.is_featured && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-amber-50 text-amber-800 rounded">
                          <Star className="h-3 w-3 fill-amber-500 text-amber-500" aria-hidden="true" />{t('jobDetails.featured')}
                        </span>
                      )}
                      {(job.badge === 'verified' || job.company_verified) && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-blue-50 text-blue-800 rounded" data-testid="job-verified-badge">
                          <ShieldCheck className="h-3 w-3" aria-hidden="true" />{t('jobDetails.verified')}
                        </span>
                      )}
                      {job.badge === 'premium' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-purple-50 text-purple-800 rounded">
                          <Crown className="h-3 w-3" aria-hidden="true" />{t('jobDetails.premium')}
                        </span>
                      )}
                    </div>
                    <h1 className="text-[26px] sm:text-[32px] lg:text-[38px] font-bold tracking-tighter text-slate-900 leading-[1.1] mb-2" data-testid="job-title">
                      {jobTitle}
                    </h1>
                    {job.company_name && (
                      <p className="text-[15px] sm:text-base text-slate-700 font-medium flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-slate-500" aria-hidden="true" />
                        {job.employer_id ? (
                          <Link to={`/empresa/${job.employer_id}`} className="hover:text-blue-700 hover:underline" data-testid="job-employer-link">
                            {job.company_name}
                          </Link>
                        ) : <span>{job.company_name}</span>}
                      </p>
                    )}
                    <ShareJobButtons jobId={job.id} title={jobTitle} company={job.company_name} />
                  </div>
                  <div className="flex flex-none items-start gap-1.5">
                    <button
                      type="button"
                      onClick={async () => {
                        const url = window.location.href;
                        const shareData = { title: jobTitle, text: jobTitle + (job.company_name ? ' · ' + job.company_name : ''), url };
                        try {
                          if (navigator.share) await navigator.share(shareData);
                          else { await navigator.clipboard.writeText(url); setFeedback(t('jobDetails.linkCopied')); setTimeout(() => setFeedback(''), 2500); }
                        } catch (_e) { /* noop */ }
                      }}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition"
                      aria-label={t('jobDetails.shareAria')}
                      data-testid="share-job-btn"
                    >
                      <Share2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <FavouriteButton jobId={job.id} className="flex-none" />
                  </div>
                </div>

                {/* Apply CTA inline on top — visible at-a-glance, even before scrolling */}
                {!applied && !expired && (
                  <div className="mt-4 lg:hidden">
                    <Button
                      onClick={handleApplyClick}
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11 font-semibold shadow-sm"
                      data-testid="apply-btn-header"
                    >
                      <Send className="h-4 w-4 mr-2" aria-hidden="true" />{t('jobDetails.applyHeader')}
                    </Button>
                    <p className="text-center text-[11.5px] text-slate-500 mt-1.5">
                      <CheckCircle2 className="h-3 w-3 inline text-emerald-600 mr-1" aria-hidden="true" />{t('jobDetails.freeDirect')}
                    </p>
                  </div>
                )}

                {/* Meta strip */}
                <dl className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="job-quick-facts">
                  {job.location && (
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                      <dt className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-0.5">{t('jobDetails.location')}</dt>
                      <dd className="text-[14px] font-semibold text-slate-900 leading-tight flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-slate-400 flex-none" aria-hidden="true" />
                        <span className="truncate">{job.location}</span>
                      </dd>
                    </div>
                  )}
                  {salary ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2.5">
                      <dt className="text-[11px] uppercase tracking-wide font-semibold text-emerald-700 mb-0.5">{t('jobDetails.salary')}</dt>
                      <dd className="text-[14px] font-bold text-emerald-900 leading-tight flex items-center gap-1.5">
                        <Banknote className="h-3.5 w-3.5 text-emerald-600 flex-none" aria-hidden="true" />
                        <span className="truncate">{salary}</span>
                      </dd>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                      <dt className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-0.5">{t('jobDetails.salary')}</dt>
                      <dd className="text-[13px] text-slate-500 leading-tight italic">{t('jobDetails.salaryTBD')}</dd>
                    </div>
                  )}
                  {employmentLabel && (
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                      <dt className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-0.5">{t('jobDetails.type')}</dt>
                      <dd className="text-[14px] font-semibold text-slate-900 leading-tight flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-slate-400 flex-none" aria-hidden="true" />
                        <span className="truncate">{employmentLabel}</span>
                      </dd>
                    </div>
                  )}
                  {job.category && (
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                      <dt className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-0.5">{t('jobDetails.category')}</dt>
                      <dd className="text-[14px] font-semibold text-slate-900 leading-tight flex items-center gap-1.5">
                        <Briefcase className="h-3.5 w-3.5 text-slate-400 flex-none" aria-hidden="true" />
                        <span className="truncate">{jobCategory}</span>
                      </dd>
                    </div>
                  )}
                </dl>
                <JobWorkInfo job={job} />

                {/* Secondary meta (small) */}
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-slate-500">
                  {postedLabel && (
                    <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" aria-hidden="true" />{postedLabel}</span>
                  )}
                  {(job.vacancies_count ?? 1) > 1 && (
                    <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold" data-testid="vacancies-badge">
                      <Users className="h-3.5 w-3.5" aria-hidden="true" />{t('jobDetails.vacanciesOpen', { count: job.vacancies_count })}
                    </span>
                  )}
                </div>
                </div>
              </header>

              {/* CV reminder banner — elegant nudge before applying */}
              {isCandidate && !hasCV && !applied && !expired && (
                <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50/60 p-4 flex items-start gap-3" data-testid="cv-reminder-banner">
                  <Lightbulb className="h-5 w-5 text-amber-600 flex-none mt-0.5" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] text-amber-900 font-semibold mb-0.5">{t('jobDetails.cvReminderTitle')}</p>
                    <p className="text-[13px] text-amber-800/90 leading-snug">
                      {t('jobDetails.cvReminderBody')} <Link to="/candidate/profile" className="font-semibold text-amber-900 underline underline-offset-2">{t('jobDetails.cvReminderCta')}</Link>.
                    </p>
                  </div>
                </div>
              )}

              {/* Sections */}
              <Card className="p-6 sm:p-8 border-slate-200 space-y-8">
                {/* Thin-content fallback callout */}
                {(!jobDescription || jobDescription.trim().length < 120) && (
                  <section className="rounded-xl bg-blue-50/60 border border-blue-100 p-4 flex items-start gap-3" data-testid="thin-content-callout">
                    <MessageCircle className="h-5 w-5 text-blue-600 flex-none mt-0.5" aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="text-[14px] font-semibold text-blue-900 leading-tight mb-0.5">{t('jobDetails.thinContentTitle')}</p>
                      <p className="text-[13px] text-blue-900/80 leading-snug">
                        {t('jobDetails.thinContentBody')}
                      </p>
                    </div>
                  </section>
                )}

                {jobDescription && (
                  <section aria-labelledby="sec-desc">
                    <h2 id="sec-desc" className="text-[15px] font-bold uppercase tracking-[0.08em] text-slate-500 mb-3">{t('jobDetails.secDesc')}</h2>
                    <RichText text={jobDescription} />
                  </section>
                )}
                {jobRequirements && (
                  <section aria-labelledby="sec-req" className="pt-6 border-t border-slate-100">
                    <h2 id="sec-req" className="text-[15px] font-bold uppercase tracking-[0.08em] text-slate-500 mb-3">{t('jobDetails.secReq')}</h2>
                    <RichText text={jobRequirements} />
                  </section>
                )}
                {jobBenefits && (
                  <section aria-labelledby="sec-ben" className="pt-6 border-t border-slate-100">
                    <h2 id="sec-ben" className="text-[15px] font-bold uppercase tracking-[0.08em] text-slate-500 mb-3">{t('jobDetails.secBen')}</h2>
                    <RichText text={jobBenefits} />
                  </section>
                )}

                {/* Informações práticas */}
                <section aria-labelledby="sec-info" className="pt-6 border-t border-slate-100" data-testid="job-practical-info">
                  <h2 id="sec-info" className="text-[15px] font-bold uppercase tracking-[0.08em] text-slate-500 mb-3">{t('jobDetails.secInfo')}</h2>
                  <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-[14px]">
                    {employmentLabel && (
                      <div className="flex items-start justify-between gap-3 py-1 border-b border-dashed border-slate-100">
                        <dt className="text-slate-500">{t('jobDetails.infoContractType')}</dt>
                        <dd className="font-semibold text-slate-900 text-right">{employmentLabel}</dd>
                      </div>
                    )}
                    {job.category && (
                      <div className="flex items-start justify-between gap-3 py-1 border-b border-dashed border-slate-100">
                        <dt className="text-slate-500">{t('jobDetails.infoCategory')}</dt>
                        <dd className="font-semibold text-slate-900 text-right">{jobCategory}</dd>
                      </div>
                    )}
                    {job.location && (
                      <div className="flex items-start justify-between gap-3 py-1 border-b border-dashed border-slate-100">
                        <dt className="text-slate-500">{t('jobDetails.infoLocation')}</dt>
                        <dd className="font-semibold text-slate-900 text-right">{job.location}</dd>
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-3 py-1 border-b border-dashed border-slate-100">
                      <dt className="text-slate-500">{t('jobDetails.infoVacancies')}</dt>
                      <dd className="font-semibold text-slate-900 text-right">{job.vacancies_count || 1}</dd>
                    </div>
                    {salary && (
                      <div className="flex items-start justify-between gap-3 py-1 border-b border-dashed border-slate-100">
                        <dt className="text-slate-500">{t('jobDetails.infoSalary')}</dt>
                        <dd className="font-semibold text-slate-900 text-right">{salary}</dd>
                      </div>
                    )}
                    {postedLabel && (
                      <div className="flex items-start justify-between gap-3 py-1 border-b border-dashed border-slate-100">
                        <dt className="text-slate-500">{t('jobDetails.infoPosted')}</dt>
                        <dd className="font-semibold text-slate-900 text-right">{postedLabel}</dd>
                      </div>
                    )}
                    {job.expires_at && !expired && (
                      <div className="flex items-start justify-between gap-3 py-1 border-b border-dashed border-slate-100">
                        <dt className="text-slate-500">{t('jobDetails.infoExpires')}</dt>
                        <dd className="font-semibold text-slate-900 text-right">{new Date(job.expires_at).toLocaleDateString(dateLocale)}</dd>
                      </div>
                    )}
                  </dl>
                </section>

                {job.company_name && (
                  <section aria-labelledby="sec-co" className="pt-6 border-t border-slate-100">
                    <h2 id="sec-co" className="text-[15px] font-bold uppercase tracking-[0.08em] text-slate-500 mb-3">{t('jobDetails.secCompany')}</h2>
                    <p className="text-[15px] text-slate-800 leading-[1.6]">
                      <strong className="text-slate-900">{job.company_name}</strong>
                      {job.location ? <> · {job.location}</> : null}
                    </p>
                    {job.employer_id && (
                      <Link to={`/empresa/${job.employer_id}`} className="inline-flex items-center gap-1 mt-3 text-[13px] font-semibold text-blue-700 hover:text-blue-900">
                        {t('jobDetails.viewCompanyFull')}
                      </Link>
                    )}
                  </section>
                )}
              </Card>

              {/* Inline apply card (mobile primary, desktop secondary) */}
              <div className="mt-6 lg:hidden">
                {applyCard(true)}
              </div>

              {/* Trust strip */}
              <JobSafetyNotice jobId={job.id} />
              <section className="mt-8" aria-labelledby="sec-trust" data-testid="trust-strip">
                <h2 id="sec-trust" className="sr-only">{t('jobDetails.trustHeading')}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3">
                    <div className="flex-none w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-[14px] leading-tight">{t('jobDetails.trustFreeTitle')}</p>
                      <p className="text-[12.5px] text-slate-500 leading-snug mt-0.5">{t('jobDetails.trustFreeBody')}</p>
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3">
                    <div className="flex-none w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-blue-600" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-[14px] leading-tight">{t('jobDetails.trustDirectTitle')}</p>
                      <p className="text-[12.5px] text-slate-500 leading-snug mt-0.5">{t('jobDetails.trustDirectBody')}</p>
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3">
                    <div className="flex-none w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
                      <ShieldCheck className="h-5 w-5 text-indigo-600" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-[14px] leading-tight">{t('jobDetails.trustVerifiedTitle')}</p>
                      <p className="text-[12.5px] text-slate-500 leading-snug mt-0.5">{t('jobDetails.trustVerifiedBody')}</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Similar jobs */}
              {similar.length > 0 && (
                <section aria-labelledby="sec-similar" className="mt-10" data-testid="similar-jobs-section">
                  <div className="flex items-end justify-between mb-4 gap-3">
                    <h2 id="sec-similar" className="text-[15px] font-bold uppercase tracking-[0.08em] text-slate-500">{t('jobDetails.similarTitle')}</h2>
                    <Link to={`/jobs/search${job.category ? `?category=${encodeURIComponent(job.category)}` : ''}`} className="text-[13px] font-semibold text-blue-700 hover:text-blue-900 inline-flex items-center gap-1">
                      {t('jobDetails.similarSeeAll')} <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </Link>
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {similar.map((s) => {
                      const sSalary = s.salary_range_min && s.salary_range_max
                        ? `${Number(s.salary_range_min).toLocaleString(dateLocale)} – ${Number(s.salary_range_max).toLocaleString(dateLocale)} ${s.currency || 'CVE'}`
                        : null;
                      const sEmp = s.employment_type ? t(`jobDetails.employment.${s.employment_type}`, { defaultValue: s.employment_type }) : null;
                      const sTitle = pickLocalized(s, 'title', lang);
                      return (
                        <li key={s.id} className="contents">
                          <Link
                            to={`/jobs/${s.id}`}
                            className="block bg-white border border-slate-200 hover:border-slate-900 hover:shadow-md rounded-xl p-4 transition group"
                            data-testid={`similar-job-${s.id}`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <h3 className="text-[15px] font-bold text-slate-900 leading-snug group-hover:text-blue-700 line-clamp-2">{sTitle}</h3>
                              {s.is_featured && (
                                <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500 flex-none mt-1" aria-hidden="true" />
                              )}
                            </div>
                            {s.company_name && (
                              <p className="text-[12.5px] text-slate-600 mb-2 truncate">{s.company_name}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-500">
                              {s.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" aria-hidden="true" />{s.location}</span>}
                              {sEmp && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" aria-hidden="true" />{sEmp}</span>}
                            </div>
                            {sSalary && (
                              <p className="mt-2 text-[12.5px] font-semibold text-emerald-700">{sSalary}</p>
                            )}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {/* FAQ — universal content that adds density and trust */}
              <section className="mt-8" aria-labelledby="sec-faq" data-testid="faq-section">
                <h2 id="sec-faq" className="text-[15px] font-bold uppercase tracking-[0.08em] text-slate-500 mb-4">{t('jobDetails.faqTitle')}</h2>
                <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden">
                  {[1, 2, 3, 4].map((idx) => (
                    <details key={idx} className="group" data-testid={`faq-item-${idx - 1}`}>
                      <summary className="cursor-pointer list-none flex items-center justify-between gap-4 px-5 py-4 hover:bg-slate-50 transition">
                        <span className="text-[14.5px] font-semibold text-slate-900">{t(`jobDetails.faqQ${idx}`)}</span>
                        <ChevronDown className="h-4 w-4 text-slate-400 group-open:rotate-180 transition flex-none" aria-hidden="true" />
                      </summary>
                      <div className="px-5 pb-4 -mt-1 text-[13.5px] text-slate-600 leading-relaxed">
                        {t(`jobDetails.faqA${idx}`)}
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              {/* CTA secundário: continuar a explorar */}
              {!applied && (
                <section className="mt-8" data-testid="explore-cta">
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 sm:p-7">
                    <div className="absolute -right-12 -top-12 w-40 h-40 rounded-full bg-blue-500/20 blur-2xl" aria-hidden="true" />
                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                      <div className="max-w-xl">
                        <p className="text-[11px] font-bold uppercase tracking-widest text-blue-300 mb-1">{t('jobDetails.exploreEyebrow')}</p>
                        <h2 className="text-[20px] sm:text-[22px] font-bold leading-tight tracking-tight">
                          {job.category
                            ? <Trans i18nKey="jobDetails.exploreTitleCat" values={{ category: job.category }} components={{ 1: <span className="text-blue-300" /> }} />
                            : t('jobDetails.exploreTitleGeneric')}
                        </h2>
                        <p className="text-[13.5px] text-slate-300 mt-1.5 leading-relaxed">
                          {t('jobDetails.exploreBody')}
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 flex-none w-full sm:w-auto">
                        <Button
                          onClick={() => navigate(`/jobs/search${job.category ? `?category=${encodeURIComponent(job.category)}` : ''}`)}
                          className="bg-white text-slate-900 hover:bg-slate-100 font-semibold"
                          data-testid="explore-search-btn"
                        >
                          <Search className="h-4 w-4 mr-2" aria-hidden="true" />{t('jobDetails.exploreSearch')}
                        </Button>
                        {!user && (
                          <Button
                            onClick={() => navigate('/candidate/register')}
                            variant="outline"
                            className="bg-transparent border-white/30 text-white hover:bg-white/10 font-semibold"
                            data-testid="explore-register-btn"
                          >
                            {t('jobDetails.exploreRegister')}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </article>

            {/* ─── Sidebar apply (sticky on desktop) ─── */}
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                {applyCard(true)}
              </div>
            </aside>
          </div>
        </div>

        {/* ─── Mobile sticky apply bar ─── */}
        {!applied && !expired && (
          <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 shadow-[0_-4px_12px_rgba(15,23,42,0.06)] p-3" data-testid="mobile-sticky-apply">
            <Button
              onClick={handleApplyClick}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white h-12 text-[15px] font-semibold"
              data-testid="apply-btn-mobile"
            >
              <Send className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('jobDetails.stickyApply')}
            </Button>
          </div>
        )}

        {/* Apply modal */}
        {showApplyModal && (
          <div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            data-testid="apply-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="apply-modal-title"
            onClick={(e) => { if (e.target === e.currentTarget) setShowApplyModal(false); }}
          >
            <Card className="p-0 max-w-md w-full border-slate-200 shadow-2xl overflow-hidden" data-testid="apply-modal-card">
              {applied ? (
                /* ════════ SUCCESS STATE ════════ */
                <div className="p-6 sm:p-7 text-center" data-testid="apply-success">
                  <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 mb-4">
                    <div className="absolute inset-0 rounded-full bg-emerald-100 animate-ping opacity-50" aria-hidden="true"></div>
                    <CheckCircle2 className="relative h-9 w-9 text-emerald-600" aria-hidden="true" />
                  </div>
                  <h3 id="apply-modal-title" className="text-[22px] font-bold tracking-tight text-slate-900 mb-1.5">{t('jobDetails.modalApplied')}</h3>
                  <p className="text-[14px] text-slate-600 leading-relaxed mb-5 max-w-sm mx-auto">
                    {job.company_name
                      ? <Trans i18nKey="jobDetails.modalAppliedCompany" values={{ company: job.company_name }} components={{ strong: <strong className="text-slate-900" /> }} />
                      : t('jobDetails.modalAppliedGeneric')}
                  </p>

                  {/* What happens next */}
                  <div className="text-left bg-slate-50 border border-slate-100 rounded-xl p-4 mb-5">
                    <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-3">{t('jobDetails.modalNextHeader')}</p>
                    <ol className="space-y-2.5">
                      <li className="flex gap-3 items-start">
                        <Mail className="h-4 w-4 text-blue-600 flex-none mt-0.5" aria-hidden="true" />
                        <span className="text-[13px] text-slate-700 leading-snug">{t('jobDetails.modalNext1')}</span>
                      </li>
                      <li className="flex gap-3 items-start">
                        <Clock className="h-4 w-4 text-blue-600 flex-none mt-0.5" aria-hidden="true" />
                        <span className="text-[13px] text-slate-700 leading-snug">{t('jobDetails.modalNext2')}</span>
                      </li>
                      <li className="flex gap-3 items-start">
                        <MessageCircle className="h-4 w-4 text-blue-600 flex-none mt-0.5" aria-hidden="true" />
                        <span className="text-[13px] text-slate-700 leading-snug">{t('jobDetails.modalNext3')}</span>
                      </li>
                    </ol>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => navigate('/my-applications')}
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                      data-testid="success-see-apps-btn"
                    >
                      {t('jobDetails.seeMyApps')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setShowApplyModal(false); navigate('/jobs/search'); }}
                      className="w-full border-slate-300"
                      data-testid="success-search-more-btn"
                    >
                      <Search className="h-4 w-4 mr-2" aria-hidden="true" />{t('jobDetails.searchMore')}
                    </Button>
                    <button
                      type="button"
                      onClick={() => setShowApplyModal(false)}
                      className="text-[12.5px] text-slate-500 hover:text-slate-800 mt-1 py-1"
                      data-testid="success-close-btn"
                    >
                      {t('jobDetails.modalClose')}
                    </button>
                  </div>
                </div>
              ) : (
                /* ════════ FORM STATE ════════ */
                <div data-testid="apply-form">
                  {/* Header */}
                  <div className="px-6 pt-6 pb-4 border-b border-slate-100">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-widest font-bold text-blue-600 mb-1">{t('jobDetails.modalFormEyebrow')}</p>
                        <h3 id="apply-modal-title" className="text-[20px] font-bold tracking-tight text-slate-900 leading-tight">{t('jobDetails.modalFormTitle')}</h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowApplyModal(false)}
                        className="text-slate-400 hover:text-slate-700 flex-none -mr-1.5 -mt-1.5 p-1.5"
                        aria-label={t('jobDetails.modalCloseAria')}
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <p className="text-[13px] text-slate-600 mt-2 truncate">
                      <Trans
                        i18nKey="jobDetails.modalFormSubtitle"
                        values={{ title: jobTitle + (job.company_name ? ` · ${job.company_name}` : '') }}
                        components={{ strong: <strong className="text-slate-900" /> }}
                      />
                    </p>
                  </div>

                  <div className="px-6 py-5 space-y-4">
                    {/* What will be sent — profile preview */}
                    <div data-testid="apply-preview">
                      <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">{t('jobDetails.modalReceiveHeader')}</p>
                      <ul className="bg-slate-50 border border-slate-100 rounded-xl divide-y divide-slate-100 overflow-hidden">
                        <li className="flex items-center gap-2 px-3.5 py-2.5 text-[13px]">
                          <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-none" aria-hidden="true" />
                          <span className="text-slate-500 flex-none">{t('jobDetails.modalNameLabel')}</span>
                          <span className="font-semibold text-slate-900 truncate">{user?.full_name || user?.email}</span>
                        </li>
                        <li className="flex items-center gap-2 px-3.5 py-2.5 text-[13px]">
                          <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-none" aria-hidden="true" />
                          <span className="text-slate-500 flex-none">{t('jobDetails.modalEmailLabel')}</span>
                          <span className="font-semibold text-slate-900 truncate">{user?.email}</span>
                        </li>
                        {user?.phone && (
                          <li className="flex items-center gap-2 px-3.5 py-2.5 text-[13px]">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-none" aria-hidden="true" />
                            <span className="text-slate-500 flex-none">{t('jobDetails.modalPhoneLabel')}</span>
                            <span className="font-semibold text-slate-900 truncate">{user.phone}</span>
                          </li>
                        )}
                        <li className="flex items-center gap-2 px-3.5 py-2.5 text-[13px]">
                          {hasCV ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-none" aria-hidden="true" />
                              <span className="text-slate-500 flex-none">{t('jobDetails.modalCvLabel')}</span>
                              <span className="font-semibold text-slate-900 truncate">{t('jobDetails.modalCvAttached')}</span>
                            </>
                          ) : (
                            <>
                              <AlertTriangle className="h-4 w-4 text-amber-500 flex-none" aria-hidden="true" />
                              <span className="text-slate-500 flex-none">{t('jobDetails.modalCvLabel')}</span>
                              <span className="text-amber-700 truncate flex-1">{t('jobDetails.modalCvMissing')} <Link to="/candidate/profile" className="font-semibold underline">{t('jobDetails.modalCvAdd')}</Link></span>
                            </>
                          )}
                        </li>
                      </ul>
                    </div>

                    {/* Quick apply with Trabadju AI CV */}
                    <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4" data-testid="quick-apply-card">
                      <div className="flex items-start gap-3">
                        <Sparkles className="h-5 w-5 text-indigo-600 flex-none mt-0.5" aria-hidden="true" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[14px] font-bold text-slate-900">{t('jobDetails.quickApplyTitle')}</p>
                          <p className="text-[12px] text-slate-600 mt-0.5">{t('jobDetails.quickApplyHint')}</p>
                          {quickState === 'nocv' && (
                            <p className="text-[12px] text-amber-800 mt-2" data-testid="quick-apply-nocv">
                              {t('jobDetails.quickApplyNoCv')} <Link to="/candidate/cv" className="font-semibold underline">{t('jobDetails.quickApplyCreateCv')}</Link>
                            </p>
                          )}
                          {quickState === 'done' && (
                            <p className="text-[12px] text-emerald-700 mt-2 font-semibold" data-testid="quick-apply-done">{t('jobDetails.quickApplyDone')}</p>
                          )}
                        </div>
                        <Button type="button" size="sm" onClick={quickApply} disabled={quickState === 'working'} className="bg-indigo-600 hover:bg-indigo-700 text-white flex-none" data-testid="quick-apply-btn">
                          {quickState === 'working' ? t('jobDetails.quickApplyWorking') : t('jobDetails.quickApplyBtn')}
                        </Button>
                      </div>
                    </div>

                    {/* Optional message */}
                    <div>
                      <label className="text-[13px] font-semibold text-slate-700 flex items-center justify-between" htmlFor="apply-message">
                        <span>{t('jobDetails.modalMessageLabel')}</span>
                        <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">{t('jobDetails.modalMessageOptional')}</span>
                      </label>
                      <Textarea
                        id="apply-message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t('jobDetails.modalMessagePlaceholder')}
                        rows={4}
                        className="mt-1.5 text-[14px]"
                        data-testid="application-message"
                      />
                      <p className="text-[11.5px] text-slate-500 mt-1">{t('jobDetails.modalMessageHint')}</p>
                    </div>

                    {feedback && <Alert variant="destructive"><AlertDescription>{feedback}</AlertDescription></Alert>}
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 bg-slate-50/70 border-t border-slate-100">
                    <Button
                      onClick={submitApplication}
                      disabled={submitting}
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11 font-semibold shadow-sm"
                      data-testid="submit-application-btn"
                    >
                      {submitting ? (
                        <>
                          <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" aria-hidden="true"></span>
                          {t('jobDetails.modalSubmitting')}
                        </>
                      ) : (
                        <><Send className="h-4 w-4 mr-2" aria-hidden="true" />{t('jobDetails.modalSubmit')}</>
                      )}
                    </Button>
                    <div className="flex items-center justify-center gap-3 mt-2.5 text-[11.5px] text-slate-500">
                      <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-600" aria-hidden="true" />{t('jobDetails.modalTrustFree')}</span>
                      <span className="text-slate-300">·</span>
                      <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3 text-slate-400" aria-hidden="true" />{t('jobDetails.modalTrustData')}</span>
                      <span className="text-slate-300">·</span>
                      <span>{t('jobDetails.modalTrustCancel')}</span>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}
      </main>
    </>
  );
}
